<script setup>
import { ref, computed, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  agent: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);
const store = useStore();
const { t } = useI18n();

const isEdit = computed(() => !!props.agent?.id);

const name = ref(props.agent?.name || '');
const description = ref(props.agent?.description || '');
const userId = ref(props.agent?.user_id || '');
const webhookUrl = ref(props.agent?.webhook_url || '');
const isDefaultN1 = ref(props.agent?.is_default_n1 || false);
const active = ref(
  props.agent?.active !== undefined ? props.agent.active : true
);
// GK-HUB: assignments = [{ inbox_id, is_dedicated }]
// Carrega do agent ou converte legado inbox_ids -> reserva
const initialAssignments = () => {
  if (props.agent?.inbox_assignments?.length)
    return [...props.agent.inbox_assignments];
  if (props.agent?.inbox_ids?.length) {
    return props.agent.inbox_ids.map(id => ({
      inbox_id: id,
      is_dedicated: false,
    }));
  }
  return [];
};
const assignments = ref(initialAssignments());

const isInboxSelected = inboxId =>
  assignments.value.some(a => a.inbox_id === inboxId);
const isInboxDedicated = inboxId => {
  const a = assignments.value.find(x => x.inbox_id === inboxId);
  return !!(a && a.is_dedicated);
};

const uiFlags = useMapGetter('iaAgents/getUIFlags');
const agents = useMapGetter('agents/getAgents');
const inboxes = useMapGetter('inboxes/getActiveInboxes'); // GK-HUB: excludes deactivated;

const isValid = computed(
  () =>
    name.value.trim().length >= 2 &&
    userId.value &&
    webhookUrl.value.trim().length > 5
);

const isLoading = computed(
  () => uiFlags.value.isCreating || uiFlags.value.isUpdating
);

const onClose = () => emit('close');

const toggleInbox = inboxId => {
  const idx = assignments.value.findIndex(a => a.inbox_id === inboxId);
  if (idx > -1) {
    assignments.value.splice(idx, 1);
  } else {
    assignments.value.push({ inbox_id: inboxId, is_dedicated: false });
  }
};

const toggleDedicated = inboxId => {
  const a = assignments.value.find(x => x.inbox_id === inboxId);
  if (a) a.is_dedicated = !a.is_dedicated;
};

const submit = async () => {
  if (!isValid.value) return;
  const payload = {
    name: name.value.trim(),
    description: description.value.trim(),
    user_id: Number(userId.value),
    webhook_url: webhookUrl.value.trim(),
    is_default_n1: isDefaultN1.value,
    active: active.value,
    inbox_assignments: assignments.value.map(a => ({
      inbox_id: a.inbox_id,
      is_dedicated: !!a.is_dedicated,
    })),
  };

  try {
    if (isEdit.value) {
      await store.dispatch('iaAgents/update', {
        id: props.agent.id,
        ...payload,
      });
      useAlert(t('IA_AGENTS_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('iaAgents/create', payload);
      useAlert(t('IA_AGENTS_MGMT.ADD.API.SUCCESS_MESSAGE'));
    }
    onClose();
  } catch (error) {
    const fallback = isEdit.value
      ? t('IA_AGENTS_MGMT.EDIT.API.ERROR_MESSAGE')
      : t('IA_AGENTS_MGMT.ADD.API.ERROR_MESSAGE');
    useAlert(error.message || fallback);
  }
};

onBeforeMount(() => {
  store.dispatch('agents/get');
  store.dispatch('inboxes/get');
});
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="
        isEdit
          ? $t('IA_AGENTS_MGMT.EDIT.TITLE')
          : $t('IA_AGENTS_MGMT.ADD.TITLE')
      "
    />
    <form class="flex flex-wrap mx-0" @submit.prevent="submit">
      <woot-input
        v-model="name"
        class="w-full"
        :label="$t('IA_AGENTS_MGMT.FORM.NAME.LABEL')"
        :placeholder="$t('IA_AGENTS_MGMT.FORM.NAME.PLACEHOLDER')"
      />

      <woot-input
        v-model="description"
        class="w-full"
        :label="$t('IA_AGENTS_MGMT.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('IA_AGENTS_MGMT.FORM.DESCRIPTION.PLACEHOLDER')"
      />

      <label class="input-container w-full mb-4">
        <span>{{ $t('IA_AGENTS_MGMT.FORM.USER.LABEL') }}</span>
        <div class="isp-select-wrap">
          <select v-model="userId" class="isp-select">
            <option value="" disabled>
              {{ $t('IA_AGENTS_MGMT.FORM.USER.PLACEHOLDER') }}
            </option>
            <option
              v-for="agentItem in agents"
              :key="agentItem.id"
              :value="agentItem.id"
            >
              {{ agentItem.name }}
            </option>
          </select>
          <span class="isp-select-arrow i-lucide-chevron-down" />
        </div>
      </label>

      <woot-input
        v-model="webhookUrl"
        class="w-full"
        :label="$t('IA_AGENTS_MGMT.FORM.WEBHOOK_URL.LABEL')"
        :placeholder="$t('IA_AGENTS_MGMT.FORM.WEBHOOK_URL.PLACEHOLDER')"
      />

      <div class="flex items-center w-full gap-6 mb-4">
        <label class="flex items-center gap-2 cursor-pointer">
          <input v-model="isDefaultN1" type="checkbox" class="accent-current" />
          <span class="text-sm text-n-slate-12">
            {{ $t('IA_AGENTS_MGMT.FORM.DEFAULT_N1.LABEL') }}
          </span>
        </label>

        <label class="flex items-center gap-2 cursor-pointer">
          <input v-model="active" type="checkbox" class="accent-current" />
          <span class="text-sm text-n-slate-12">
            {{ $t('IA_AGENTS_MGMT.FORM.ACTIVE.LABEL') }}
          </span>
        </label>
      </div>

      <div class="w-full mb-4">
        <label class="block mb-2 text-sm font-medium text-n-slate-12">
          {{ $t('IA_AGENTS_MGMT.FORM.INBOXES.LABEL') }}
        </label>
        <p class="mb-2 text-xs text-n-slate-11">
          {{ $t('IA_AGENTS_MGMT.FORM.INBOXES.HELP') }}
        </p>
        <div
          class="flex flex-col gap-2 max-h-60 overflow-y-auto p-2 border rounded-lg border-n-weak bg-n-solid-2"
        >
          <div
            v-for="inbox in inboxes"
            :key="inbox.id"
            class="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md"
            :class="
              isInboxSelected(inbox.id) ? 'bg-n-solid-3' : 'bg-transparent'
            "
          >
            <label class="flex items-center gap-2 cursor-pointer flex-1">
              <input
                type="checkbox"
                :checked="isInboxSelected(inbox.id)"
                class="accent-current"
                @change="toggleInbox(inbox.id)"
              />
              <span class="text-sm text-n-slate-12">{{ inbox.name }}</span>
            </label>

            <button
              v-if="isInboxSelected(inbox.id)"
              type="button"
              class="px-2 py-0.5 rounded-md text-xs font-medium transition-colors"
              :class="
                isInboxDedicated(inbox.id)
                  ? 'bg-n-brand text-white'
                  : 'bg-n-solid-1 text-n-slate-11 border border-n-weak'
              "
              @click="toggleDedicated(inbox.id)"
            >
              {{
                isInboxDedicated(inbox.id)
                  ? $t('IA_AGENTS_MGMT.FORM.INBOXES.DEDICATED_LABEL')
                  : $t('IA_AGENTS_MGMT.FORM.INBOXES.RESERVE_LABEL')
              }}
            </button>
          </div>
          <span v-if="!inboxes?.length" class="text-xs text-n-slate-11">
            {{ $t('IA_AGENTS_MGMT.FORM.INBOXES.EMPTY') }}
          </span>
        </div>
      </div>

      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('IA_AGENTS_MGMT.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="
            isEdit
              ? $t('IA_AGENTS_MGMT.FORM.UPDATE')
              : $t('IA_AGENTS_MGMT.FORM.CREATE')
          "
          :disabled="!isValid || isLoading"
          :is-loading="isLoading"
        />
      </div>
    </form>
  </div>
</template>

<style scoped>
/* GK-HUB: Select styles — copied from IspConversas.vue (reports pattern) */
.isp-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 100%;
}
.isp-select-arrow {
  position: absolute;
  right: 9px;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  pointer-events: none;
  color: #9ca3af;
  flex-shrink: 0;
}
.isp-select {
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  width: 100%;
  height: auto;
  margin: 0;
  font-size: 0.875rem;
  font-family: inherit;
  line-height: 1.5;
  padding: 8px 30px 8px 10px;
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  transition:
    border-color 150ms ease,
    background-color 150ms ease;
  background: none;
  background-color: #f8f9fb;
  color: #4b5563;
  border: 1px solid #e0e2e6;
}
.isp-select:hover {
  border-color: #cbd0d6;
}
.isp-select:focus {
  border-color: #2e86c1;
}
body.dark .isp-select {
  background-color: rgb(16, 22, 32) !important;
  background: rgb(16, 22, 32) !important;
  border-color: rgba(255, 255, 255, 0.08);
  color: rgb(var(--s-50));
  font-size: 0.875rem;
}
body.dark .isp-select:hover {
  border-color: rgba(255, 255, 255, 0.14);
  background-color: rgba(255, 255, 255, 0.03);
}
body.dark .isp-select-arrow {
  color: #64748b;
}
</style>
